/**
 * ChoirEventsTab — Events & QR panel of Choir Ministry section.
 * Pure presentation component; all data + handlers passed in via props.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Plus, Trash2, Eye, BookOpen, Music, QrCode, RefreshCw, Send, Power, PowerOff, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const adminHeaders = () => {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || '';
  return t ? { 'x-admin-token': t } : {};
};

export default function ChoirEventsTab({
  // QR / venue token
  venueToken,
  rotatingVenueToken,
  onRotateVenueToken,
  // Create event form
  newEventType,
  setNewEventType,
  newEventDate,
  setNewEventDate,
  newEventTime,
  setNewEventTime,
  newEventTitle,
  setNewEventTitle,
  eventCreating,
  onCreateEvent,
  // Events list
  choirEvents,
  onViewEventAttendance,
  onDeleteEvent,
  onSendEventReminder,
  // Master switch
  isMainAdmin,
}) {
  const baseUrl = `${window.location.origin}/choir-checkin`;
  const fullUrl = venueToken ? `${baseUrl}?venue=${venueToken}` : baseUrl;

  // ---- Master events-module switch ----
  const [moduleStatus, setModuleStatus] = useState({ enabled: true, updated_at: null, updated_by: null });
  const [moduleLoading, setModuleLoading] = useState(true);
  const [moduleToggling, setModuleToggling] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/choir/events/module/status`);
        if (active) setModuleStatus(res.data);
      } catch (e) { /* default to enabled */ }
      if (active) setModuleLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const toggleModule = async () => {
    if (!isMainAdmin || moduleToggling) return;
    const next = !moduleStatus.enabled;
    const verb = next ? 'TURN ON' : 'TURN OFF';
    if (!window.confirm(
      `${verb} the entire Choir Events module?\n\n` +
      (next
        ? 'Check-ins will resume, auto Sunday seeding will run, and 24h/3h reminders will fire again.'
        : 'Check-ins will be blocked, auto Sunday seeding will pause, and 24h/3h reminders will NOT fire.')
    )) return;
    setModuleToggling(true);
    try {
      const res = await axios.put(`${API}/choir/events/module/status`, { enabled: next }, { headers: adminHeaders() });
      setModuleStatus({
        enabled: res.data.enabled,
        updated_at: new Date().toISOString(),
        updated_by: 'you',
      });
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Could not change the switch. Only the Main Admin can flip it.');
    }
    setModuleToggling(false);
  };

  return (
    <div className="space-y-6" data-testid="events-panel">
      {/* MASTER SWITCH — Main Admin only */}
      {isMainAdmin && (
        <div
          className={`rounded-2xl p-5 border-2 flex items-center justify-between gap-4 ${
            moduleStatus.enabled
              ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-300'
              : 'bg-gradient-to-r from-rose-50 to-red-50 border-rose-300'
          }`}
          data-testid="choir-events-module-card"
        >
          <div className="flex items-center gap-3">
            {moduleStatus.enabled ? (
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Power className="w-6 h-6" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-lg shadow-rose-500/30">
                <PowerOff className="w-6 h-6" />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Choir Events Module</p>
              <h3 className={`text-lg font-bold ${moduleStatus.enabled ? 'text-emerald-800' : 'text-rose-800'}`}>
                {moduleLoading ? 'Loading…' : moduleStatus.enabled ? 'ON — all event functions active' : 'OFF — events paused'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {moduleStatus.enabled
                  ? 'Check-ins, auto Sunday seeding, and 24h/3h reminder emails are all running.'
                  : 'Check-ins blocked. Auto Sunday seeding paused. 24h/3h reminders will NOT send.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={moduleStatus.enabled}
            disabled={moduleLoading || moduleToggling}
            onClick={toggleModule}
            data-testid="choir-events-module-toggle"
            className={`relative inline-flex h-9 w-16 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:opacity-50 ${
              moduleStatus.enabled ? 'bg-emerald-500' : 'bg-slate-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-flex items-center justify-center h-8 w-8 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                moduleStatus.enabled ? 'translate-x-7' : 'translate-x-0'
              }`}
            >
              {moduleToggling && <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />}
            </span>
          </button>
        </div>
      )}
      {/* QR Code */}
      <div className="bg-gradient-to-br from-violet-900 to-purple-800 text-white rounded-2xl p-6 border-2 border-amber-400">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="bg-white p-4 rounded-2xl shadow-2xl">
            <QRCodeSVG value={fullUrl} size={180} level="M" includeMargin={false} />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/20 border border-amber-300 mb-3">
              <QrCode className="w-4 h-4 text-amber-300" />
              <span className="text-xs font-bold text-amber-200">CHOIR ATTENDANCE QR</span>
            </div>
            <h3 className="text-2xl font-bold mb-2">Scan to Mark Attendance</h3>
            <p className="text-amber-100/80 text-sm mb-3">
              <strong>Print and display</strong> this QR at the church. Members who scan this on-site QR mark attendance instantly — no GPS required. Members opening the link any other way must enable GPS and be physically at the church.
            </p>
            <div className="bg-black/30 rounded-lg p-3 font-mono text-xs text-amber-200 break-all">
              {fullUrl}
            </div>
            <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
              <button
                onClick={() => navigator.clipboard.writeText(fullUrl)}
                className="px-4 py-2 rounded-lg bg-white text-violet-900 text-sm font-bold hover:bg-amber-100"
                data-testid="copy-choir-qr-link"
              >
                Copy Link
              </button>
              <button
                onClick={() => window.open(fullUrl, '_blank')}
                className="px-4 py-2 rounded-lg bg-amber-500 text-violet-950 text-sm font-bold hover:bg-amber-400"
              >
                Open Page
              </button>
              <button
                onClick={onRotateVenueToken}
                disabled={rotatingVenueToken}
                className="px-4 py-2 rounded-lg bg-white/10 border border-amber-300/50 text-amber-100 text-sm font-bold hover:bg-white/20 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="rotate-venue-token-btn"
                title="Generate a new QR — old printed QRs stop working. Use this if a poster is stolen or leaked."
              >
                <RefreshCw className={`w-3.5 h-3.5 ${rotatingVenueToken ? 'animate-spin' : ''}`} />
                Regenerate QR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create New Event */}
      <div className="bg-white rounded-2xl p-6 shadow border-2 border-violet-100">
        <h3 className="font-bold text-violet-900 text-lg mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" /> Log New Event
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={newEventType}
            onChange={e => setNewEventType(e.target.value)}
            data-testid="new-event-type"
            className="px-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-violet-400 outline-none"
          >
            <option value="rehearsal">Rehearsal</option>
            <option value="sunday">Sunday Service</option>
          </select>
          <input
            type="date"
            value={newEventDate}
            onChange={e => setNewEventDate(e.target.value)}
            data-testid="new-event-date"
            className="px-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-violet-400 outline-none"
          />
          <input
            type="time"
            value={newEventTime}
            onChange={e => setNewEventTime(e.target.value)}
            data-testid="new-event-time"
            className="px-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-violet-400 outline-none"
          />
          <input
            type="text"
            value={newEventTitle}
            onChange={e => setNewEventTitle(e.target.value)}
            placeholder="Title (optional)"
            data-testid="new-event-title"
            className="px-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-violet-400 outline-none"
          />
          <button
            onClick={onCreateEvent}
            disabled={eventCreating}
            data-testid="create-event-btn"
            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 font-bold shadow hover:from-amber-400 disabled:opacity-50"
          >
            {eventCreating ? '...' : 'Create'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Sunday services are auto-created weekly at 10:00 AM. Add one-off rehearsals here.
          Scan window: 60 min before to 3 hr after start time.
        </p>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-2xl shadow border overflow-hidden">
        <div className="bg-violet-50 px-4 py-3 border-b">
          <h3 className="font-bold text-violet-900">All Events ({choirEvents.length})</h3>
        </div>
        <div className="divide-y">
          {choirEvents.length === 0 && (
            <p className="text-center text-slate-500 py-8">No events yet. Create one above.</p>
          )}
          {choirEvents.map(ev => (
            <div key={ev.id} className="flex items-center gap-3 px-4 py-3 hover:bg-violet-50/30" data-testid={`event-row-${ev.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${ev.type === 'sunday' ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'}`}>
                {ev.type === 'sunday' ? <BookOpen className="w-5 h-5" /> : <Music className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{ev.title}</p>
                <p className="text-xs text-slate-500">
                  {ev.date} at {ev.start_time} · {ev.attendance_count || 0} marked present
                  {ev.auto && <span className="ml-2 text-[10px] text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">AUTO</span>}
                </p>
              </div>
              <button
                onClick={() => onViewEventAttendance(ev)}
                className="p-2 rounded-lg hover:bg-violet-100 text-violet-700"
                title="View attendance"
              >
                <Eye className="w-4 h-4" />
              </button>
              {onSendEventReminder && (
                <button
                  onClick={() => onSendEventReminder(ev.id, `${ev.title} · ${ev.date} ${ev.start_time}`)}
                  className="p-2 rounded-lg hover:bg-amber-50 text-amber-600"
                  title="Send pre-event reminder + check-in link to all choir members now"
                  data-testid={`send-reminder-${ev.id}`}
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onDeleteEvent(ev.id)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                data-testid={`delete-event-${ev.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
